import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig, history } from '@umijs/max';
import { RESPONSE_CODES,USER_KEY,EXPIRES_TIME_KEY,ACCESS_TOKEN_KEY } from '@/utils/constants';
import { message, notification } from 'antd';
import Settings from '../config/defaultSettings';

let isErrorShown = false; // 标志位，防止重复提示

// 携带token的白名单
const TOKEN_WHITE_LIST = [
  '/system/auth/getCurUserInfo',
  '/system/auth/logout',
];

const showError = (msg: string) => {
  if (!isErrorShown) {
    isErrorShown = true;
    message.error(msg);
    setTimeout(() => {
      isErrorShown = false; // 一段时间后允许再次显示错误
    }, 3000); // 3 秒内不重复显示
  }
};

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */


export const errorConfig: RequestConfig = {

  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {

        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        // yanxi: 自定义错误信息格式
        showError(`服务器未响应，请检查网络，稍后重试！`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        showError(`服务器未响应，请检查网络，稍后重试！`);
      } else {
        // 发送请求时出了点问题
        showError(`服务器未响应，请检查网络，稍后重试！`);
      }
    },
  },





  //请求拦截器 - v6：有token过期
  requestInterceptors: [
    (config: RequestOptions) => {
      const accessToken = sessionStorage.getItem(ACCESS_TOKEN_KEY);
      const expiresTime = sessionStorage.getItem(EXPIRES_TIME_KEY);
      const currentUser = JSON.parse(localStorage.getItem(USER_KEY) || '{}');
      // 如果是登录接口，跳过 token 检查（包含/auth并且不在白名单中的）
      if (config.url?.includes('/auth') && !TOKEN_WHITE_LIST.some((url) => config.url?.includes(url))) {
        return config;
      }

      // 检查 accessToken 是否存在
      if (accessToken && expiresTime) {
        // 包含/auth的接口请求不判断用户id，因为可能是未登录状态下的请求，其他接口如果没有用户id则跳转登录
        if (!config.url?.includes('/auth') && !currentUser?.id) {
          showError('未登录，请先登录');
          window.location.href = `${Settings.base}#/user/login`;
          return Promise.reject(new Error('No user info'));
        }
        // 检查是否过期
        if (new Date(expiresTime) <= new Date()) {
          // 如果过期，清除本地存储并提示用户重新登录
          sessionStorage.clear();
          showError('登录已过期，请重新登录');
          window.location.href = `${Settings.base}#/user/login`; // 跳转到登录页
          return Promise.reject(new Error('Token expired'));
        }

        // 如果未过期，将 accessToken 添加到请求头
        config.headers = {
          ...config.headers,
          authorization: accessToken,
        };
      } else {
        // 如果没有 accessToken，提示用户重新登录
        showError('未登录，请先登录');
        window.location.href = `${Settings.base}#/user/login`; // 跳转到登录页
        return Promise.reject(new Error('No token found'));
      }

      return config;
    },
  ],


  // 请求拦截器-v6 ：无token过期
  // requestInterceptors: [
  //   (config: RequestOptions) => {
  //     // 拦截请求配置，进行个性化处理。
  //     const token = sessionStorage.getItem('accessToken') || localStorage.getItem('accessToken');
  //     if (token) {
  //       const headers = {
  //         ...config.headers,
  //         authorization: token, // 根据后端key值来定义
  //       };

  //       return { ...config, headers };
  //     }
  //     return config;
  //   },
  // ],


  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 如果是文件流，直接返回
      if (
        response?.config?.responseType === 'blob' ||
        response?.config?.responseType === 'arraybuffer'
      ) {
        return response;
      }

      // 拦截响应数据，进行个性化处理
      const { data } = response as unknown as ResponseStructure;

      // 提示错误信息
      if (data && data?.code !== RESPONSE_CODES.SUCCESS) {
        message.error(data.msg || '请求失败！');
      }
      if (data && data.code === RESPONSE_CODES.UNAUTHORIZED) {
        window.location.href = `${Settings.base}#/403`;
        return Promise.reject(new Error('无权限访问'));
      }
      return response;
    },
  ],
};
